import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  EmptyState,
  PageHeader,
  SectionCard,
  StatusBadge,
  TWButton,
  TWConfirmModal,
  TWDataTable,
} from '../../components/ui';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import { DataTableSkeleton, colTitle } from '../../data/columns';
import fleetApi from '../../services/fleetApi';
import AdminOnly from './components/admin-only';
import useFleetPoll, { fleetErrorText } from './use-fleet-poll';
import {
  ROLLOUT_POLL_MS,
  isTerminalRollout,
  needsTypedConfirm,
  offlineHoldText,
  relativeSeen,
  statusLabel,
  statusTone,
} from './fleet-utils';
import { ROLLOUT_TONES, appLabel } from './rollouts';

const BACK = '/dashboard/devices/rollouts';
// What Android reports when the installed app was signed with another key.
const SIGNATURE_ERROR = 'INSTALL_FAILED_UPDATE_INCOMPATIBLE';
const DASH = '—';
const ACTION_FALLBACK = 'Something went wrong. Try again.';

const PAGE_CLASS =
  'min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';
const LINK_CLASS =
  'inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700';

const CHIPS = [
  { key: 'queued', label: 'Queued', tone: 'slate' },
  { key: 'sent', label: 'Sent', tone: 'sky' },
  { key: 'acked', label: 'Acked', tone: 'blue' },
  { key: 'done', label: 'Done', tone: 'emerald', always: true },
  { key: 'failed', label: 'Failed', tone: 'rose', always: true },
  { key: 'expired', label: 'Expired', tone: 'slate' },
  { key: 'cancelled', label: 'Cancelled', tone: 'slate' },
];

const DEVICE_SKELETON = [
  { label: 'Device', w: 'w-32' },
  { label: 'Store', w: 'w-28' },
  { label: 'Batch', align: 'right', w: 'w-8' },
  { label: 'Status', shape: 'pill', w: 'w-20' },
  { label: 'Installed version', w: 'w-20' },
  { label: 'Error', w: 'w-40' },
];

const Shell = ({ children }) => (
  <div className={PAGE_CLASS}>
    <div className="mx-auto max-w-7xl space-y-6">{children}</div>
  </div>
);

const DetailSkeleton = () => (
  <Shell>
    <PageHeader
      backTo={BACK}
      backLabel="Rollouts"
      title={<SkeletonBlock className="h-8 w-56" />}
    />
    <SectionCard>
      <div className="space-y-3">
        <SkeletonBlock className="h-2 w-full" />
        <SkeletonBlock className="h-4 w-1/2" />
      </div>
    </SectionCard>
    <DataTableSkeleton headers={DEVICE_SKELETON} rows={4} />
  </Shell>
);

const NotFound = ({ description, retry }) => (
  <Shell>
    <EmptyState
      icon={ExclamationTriangleIcon}
      title="Rollout not found"
      description={description}
      action={
        <div className="flex items-center justify-center gap-2">
          <Link to={BACK} className={LINK_CLASS}>
            Back to rollouts
          </Link>
          {retry ? <TWButton onClick={retry}>Retry</TWButton> : null}
        </div>
      }
    />
  </Shell>
);

const NOT_FOUND_TEXT = 'It may have been removed.';

// Why a rollout is paused, from the API's `rollout.pause` audit row (P-13).
// No row, or an unknown reason, reads as a pause by an admin; a failed read
// claims no cause.
const bannerText = (detail, maxfailures, failed) => {
  if (failed) return 'Paused. Resume to continue.';
  switch (detail?.reason) {
    case 'unreachable':
      return `Paused: ${detail.failures ?? 0} failed, ${detail.pending ?? 0} unreachable (limit ${maxfailures}). Resume to continue; returning devices install after Resume.`;
    case 'failures':
      return `Paused: ${detail.failures ?? 0} failed (limit ${maxfailures}). Resume to continue.`;
    case 'release':
      return 'Paused: the release was removed.';
    default:
      return 'Paused by an admin. Resume to continue.';
  }
};

const RolloutDetail = () => {
  const { id } = useParams();
  // Keyed by id: a rollback lands on the new rollout with clean state. No GUID
  // gate: the API answers an unknown id with a 404, shown as "not found".
  return (
    <AdminOnly>
      <RolloutDetailView key={id} id={id} />
    </AdminOnly>
  );
};

const RolloutDetailView = ({ id }) => {
  const navigate = useNavigate();

  // Polling stops once the status the hook ACCEPTED is terminal (see the
  // effect below); an answer the hook dropped as superseded never counts.
  const [terminal, setTerminal] = useState(false);
  const { data, loading, error, stalled, reload } = useFleetPoll(
    () => fleetApi.getRollout(id).then((res) => res.data.data),
    {
      intervalMs: ROLLOUT_POLL_MS,
      deps: [id],
      poll: !terminal,
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load the rollout')),
    },
  );

  // Pause, Resume and Cancel answer with the Rollout (no devices[]): it lands
  // over the last detail until the next poll answer replaces it.
  const [patch, setPatch] = useState(null);
  useEffect(() => {
    setPatch(null);
  }, [data]);
  const rollout = data ? (patch ? { ...data, ...patch } : data) : null;
  const shownStatus = rollout?.status;
  useEffect(() => {
    setTerminal(isTerminalRollout(shownStatus));
  }, [shownStatus]);

  const [confirm, setConfirm] = useState(null); // 'cancel' | 'rollback'
  const [retryRow, setRetryRow] = useState(null); // the device row whose Retry with replace awaits confirmation
  // 'pause' | 'resume' | 'cancel' | 'rollback' | 'retry:<deviceid>'
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(false);

  const paused = rollout?.status === 'paused';
  const createdate = rollout?.createdate;
  // failed: the audit read itself failed, so the cause is unknown.
  const [pauseInfo, setPauseInfo] = useState({ settled: false, detail: null, failed: false });
  useEffect(() => {
    if (!paused) {
      setPauseInfo({ settled: false, detail: null, failed: false });
      return undefined;
    }
    let cancelled = false;
    const since = createdate ? '&startdate=' + encodeURIComponent(createdate) : '';
    fleetApi
      .listAudit('?action=rollout.pause' + since + '&pageSize=50')
      .then((res) => {
        if (cancelled) return;
        // Page 1 only (newest 50 pause rows of ALL rollouts since this one
        // started, P-13): a pause older than that falls back to the admin
        // text. Acceptable at fleet size; widen the page if it ever is not.
        const row = (res.data.data || []).find((r) => r.targetid === id);
        setPauseInfo({ settled: true, detail: row?.detail || null, failed: false });
      })
      .catch(() => {
        if (!cancelled) setPauseInfo({ settled: true, detail: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [paused, id, createdate]);

  if (error) {
    const notFound = error.response?.status === 404;
    return (
      <NotFound
        description={notFound ? NOT_FOUND_TEXT : fleetErrorText(error, NOT_FOUND_TEXT)}
        retry={notFound ? undefined : reload}
      />
    );
  }
  if (loading || !rollout) return <DetailSkeleton />;

  const { status, counts: c = {} } = rollout;
  const total = c.total ?? 0;
  const done = c.done ?? 0;
  const failed = c.failed ?? 0;
  const donePct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  const failedPct = total > 0 ? Math.min(100 - donePct, (failed / total) * 100) : 0;
  const live = status === 'running' || status === 'paused';
  const canRollBack = isTerminalRollout(status) && Boolean(rollout.previousreleaseid);
  const reasonWord =
    paused && (pauseInfo.detail?.reason === 'unreachable' || pauseInfo.detail?.reason === 'failures')
      ? pauseInfo.detail.reason
      : null;

  const runAction = async (kind) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);
    try {
      if (kind === 'rollback') {
        const res = await fleetApi.rollbackRollout(id);
        message.success('Rollback started');
        navigate(`${BACK}/${res.data.data.id}`);
        return;
      }
      const call = {
        pause: fleetApi.pauseRollout,
        resume: fleetApi.resumeRollout,
        cancel: fleetApi.cancelRollout,
      }[kind];
      const res = await call(id);
      const next = res.data.data;
      setPatch(next);
      // The reload supersedes any poll already in flight (useFleetPoll drops
      // older answers), so a stale answer cannot put the old status back.
      reload();
      message.success(
        { pause: 'Rollout paused', resume: 'Rollout resumed', cancel: 'Rollout cancelled' }[kind],
      );
    } catch (err) {
      message.error(fleetErrorText(err, ACTION_FALLBACK));
    } finally {
      busyRef.current = false;
      setBusy(null);
      setConfirm(null);
    }
  };

  // A signature mismatch can only be fixed by replacing the app: one device,
  // one batch, replace on, then on to the new rollout.
  const retryWithReplace = async (row) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('retry:' + row.deviceid);
    try {
      const res = await fleetApi.createRollout({
        releaseid: rollout.releaseid,
        target: { kind: 'devices', deviceids: [row.deviceid] },
        batchsize: 1,
        batchintervalminutes: 0,
        maxfailures: 0,
        setscurrent: false,
        replace: true,
      });
      message.success('Retry started');
      navigate(`${BACK}/${res.data.data.id}`);
    } catch (err) {
      message.error(fleetErrorText(err, 'Could not start the retry'));
    } finally {
      busyRef.current = false;
      setBusy(null);
      setRetryRow(null);
    }
  };

  // The API never replaces the agent itself, so its rows get no retry.
  const canReplace = (rollout.release?.app || rollout.app) !== 'agent';

  const columns = [
    {
      title: colTitle('Device'),
      key: 'device',
      render: (_, d) => (
        <Link
          to={`/dashboard/devices/${d.deviceid}`}
          className="text-sm font-semibold text-blue-600 hover:underline dark:text-sky-400"
        >
          {d.devicename || d.deviceid}
        </Link>
      ),
    },
    {
      title: colTitle('Store'),
      key: 'store',
      render: (_, d) => (
        <span className="text-sm text-slate-700 dark:text-slate-200">{d.storename || DASH}</span>
      ),
    },
    {
      title: colTitle('Batch', { align: 'right' }),
      key: 'batch',
      align: 'right',
      render: (_, d) => <span className="tabular-nums">{d.batchno ?? DASH}</span>,
    },
    {
      title: colTitle('Status'),
      key: 'status',
      render: (_, d) => (
        <StatusBadge size="sm" tone={statusTone(d.status)} label={statusLabel(d.status)} />
      ),
    },
    {
      title: colTitle('Installed version'),
      key: 'installed',
      render: (_, d) => (
        <span className="text-sm tabular-nums text-slate-700 dark:text-slate-200">
          {d.installedversioncode ?? DASH}
        </span>
      ),
    },
    {
      title: colTitle('Error'),
      key: 'error',
      render: (_, d) =>
        d.error ? (
          <div className="space-y-1">
            <span
              title={d.error}
              className="block max-w-xs break-words text-sm text-rose-600 dark:text-rose-400"
            >
              {d.error}
            </span>
            {canReplace && String(d.error).startsWith(SIGNATURE_ERROR) ? (
              <TWButton
                variant="default"
                size="sm"
                disabled={Boolean(busy)}
                onClick={() => setRetryRow(d)}
              >
                Retry with replace
              </TWButton>
            ) : null}
          </div>
        ) : (
          <span className="text-sm text-slate-400">{DASH}</span>
        ),
    },
  ];

  const description = (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <StatusBadge
        dot
        size="sm"
        tone={ROLLOUT_TONES[status] || 'slate'}
        label={statusLabel(status)}
      />
      <span>
        {`Started ${relativeSeen(rollout.startedat || rollout.createdate)} by ${
          rollout.createdbyemail || DASH
        }`}
      </span>
    </span>
  );

  return (
    <Shell>
      <PageHeader
        backTo={BACK}
        backLabel="Rollouts"
        title={`Rollout · ${appLabel(rollout.release?.app || rollout.app)} ${
          rollout.release?.versionname || ''
        }`.trim()}
        description={description}
        actions={
          <>
            {status === 'running' ? (
              <TWButton
                variant="default"
                loading={busy === 'pause'}
                disabled={Boolean(busy)}
                onClick={() => runAction('pause')}
              >
                Pause
              </TWButton>
            ) : null}
            {status === 'paused' ? (
              <TWButton
                variant="default"
                loading={busy === 'resume'}
                disabled={Boolean(busy)}
                onClick={() => runAction('resume')}
              >
                Resume
              </TWButton>
            ) : null}
            {live ? (
              <TWButton variant="danger" disabled={Boolean(busy)} onClick={() => setConfirm('cancel')}>
                Cancel
              </TWButton>
            ) : null}
            {canRollBack ? (
              <TWButton
                variant="default"
                disabled={Boolean(busy)}
                onClick={() => setConfirm('rollback')}
              >
                Roll back
              </TWButton>
            ) : null}
          </>
        }
      />

      {stalled ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <span>Live updates paused.</span>
          <TWButton variant="link" size="sm" onClick={reload}>
            Retry
          </TWButton>
        </div>
      ) : null}

      {paused && pauseInfo.settled ? (
        <div
          role="status"
          className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span>{bannerText(pauseInfo.detail, rollout.maxfailures, pauseInfo.failed)}</span>
        </div>
      ) : null}

      <SectionCard>
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Progress</span>
            <span className="text-sm tabular-nums text-slate-700 dark:text-slate-200">
              {`${done} of ${total} done`}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label="Rollout progress"
            aria-valuenow={done}
            aria-valuemin={0}
            aria-valuemax={total}
            className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
          >
            <div className="h-full bg-emerald-500" style={{ width: `${donePct}%` }} />
            <div className="h-full bg-rose-500" style={{ width: `${failedPct}%` }} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {CHIPS.filter((chip) => chip.always || (c[chip.key] ?? 0) > 0).map((chip) => (
              <span key={chip.key} className="inline-flex items-center gap-1.5">
                <StatusBadge size="sm" tone={chip.tone} label={`${chip.label} ${c[chip.key] ?? 0}`} />
                {chip.key === 'failed' && reasonWord ? (
                  <span className="text-xs font-medium text-rose-600 dark:text-rose-400">
                    {reasonWord}
                  </span>
                ) : null}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {`Batches of ${rollout.batchsize}, every ${rollout.batchintervalminutes} min, pauses after ${rollout.maxfailures} failures`}
          </p>
          {rollout.windowstart && rollout.windowend ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {`Installs are sent only between ${rollout.windowstart} and ${rollout.windowend} (store time).`}
            </p>
          ) : null}
          {live ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {offlineHoldText(rollout.batchintervalminutes)}
            </p>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title="Devices" noBodyPadding>
        <TWDataTable
          columns={columns}
          data={rollout.devices || []}
          rowKey="deviceid"
          empty="No devices in this rollout"
        />
      </SectionCard>

      <TWConfirmModal
        open={confirm === 'cancel'}
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => runAction('cancel')}
        loading={busy === 'cancel'}
        tone="danger"
        title="Cancel this rollout?"
        description="Queued installs are cancelled. Installs already running finish."
        confirmLabel="Cancel rollout"
        cancelLabel="Keep running"
      />
      <TWConfirmModal
        open={Boolean(retryRow)}
        onClose={() => {
          if (!busy) setRetryRow(null);
        }}
        onConfirm={() => retryWithReplace(retryRow)}
        loading={Boolean(busy) && busy.startsWith('retry:')}
        tone="danger"
        title="Replace the app and reinstall?"
        description="Loses the app's data; the kiosk must be paired again."
        confirmLabel="Replace and reinstall"
      />
      <TWConfirmModal
        open={confirm === 'rollback'}
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        onConfirm={() => runAction('rollback')}
        loading={busy === 'rollback'}
        tone="warning"
        title="Roll back this rollout?"
        description="Installs the previous release on the devices this rollout touched."
        confirmLabel="Roll back"
        requireTypedConfirmation={needsTypedConfirm(total) ? String(total) : undefined}
      />
    </Shell>
  );
};

export default RolloutDetail;
